import { apiRoute } from "./config";

export type RoadmapStage =
  | "someday_maybe"
  | "do_soon"
  | "doing_now"
  | "done";

export interface RoadmapItem {
  id: string;
  title: string;
  summary: string;
  landedInBuild: number | null;
}

export interface RoadmapColumn {
  id: RoadmapStage;
  title: string;
  description: string;
  items: RoadmapItem[];
}

export interface RoadmapSnapshot {
  currentBuild: number;
  updatedAt: string;
  columns: RoadmapColumn[];
}

export interface RoadmapRepository {
  load(): Promise<RoadmapSnapshot>;
}

export class RoadmapRepositoryError extends Error {
  constructor(
    readonly status: number | null,
    message = "roadmap_unavailable",
  ) {
    super(message);
  }
}

const expectedColumns: ReadonlyArray<{
  id: RoadmapStage;
  title: string;
}> = [
  { id: "someday_maybe", title: "Someday maybe" },
  { id: "do_soon", title: "Do soon" },
  { id: "doing_now", title: "Doing now" },
  { id: "done", title: "Done" },
];

function object(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && !Array.isArray(value) && typeof value === "object";
}

function exactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return Object.keys(value).sort().join("\u0000")
    === [...keys].sort().join("\u0000");
}

function boundedString(value: unknown, maximum: number): value is string {
  return typeof value === "string"
    && value.length > 0
    && value.length <= maximum;
}

function item(
  value: unknown,
  stage: RoadmapStage,
  currentBuild: number,
): RoadmapItem {
  if (
    !object(value)
    || !exactKeys(value, [
      "id",
      "title",
      "summary",
      "landed_in_build",
    ])
    || !boundedString(value.id, 120)
    || !boundedString(value.title, 180)
    || !boundedString(value.summary, 700)
  ) {
    throw new Error("invalid_roadmap_payload");
  }
  if (
    (stage === "done"
      && (
        typeof value.landed_in_build !== "number"
        || !Number.isSafeInteger(value.landed_in_build)
        || value.landed_in_build < 1
        || value.landed_in_build > currentBuild
      ))
    || (stage !== "done" && value.landed_in_build !== null)
  ) {
    throw new Error("invalid_roadmap_payload");
  }
  return {
    id: value.id,
    title: value.title,
    summary: value.summary,
    landedInBuild: value.landed_in_build as number | null,
  };
}

export function parseRoadmapPayload(value: unknown): RoadmapSnapshot {
  if (
    !object(value)
    || !exactKeys(value, [
      "schema_version",
      "current_build",
      "updated_at",
      "columns",
    ])
    || value.schema_version !== "public-roadmap-v1"
    || typeof value.current_build !== "number"
    || !Number.isSafeInteger(value.current_build)
    || value.current_build < 1
    || !boundedString(value.updated_at, 40)
    || !Number.isFinite(Date.parse(value.updated_at))
    || !Array.isArray(value.columns)
    || value.columns.length !== expectedColumns.length
  ) {
    throw new Error("invalid_roadmap_payload");
  }
  const ids = new Set<string>();
  const columns = value.columns.map((candidate, index): RoadmapColumn => {
    const expected = expectedColumns[index];
    if (
      !object(candidate)
      || !exactKeys(candidate, ["id", "title", "description", "items"])
      || candidate.id !== expected.id
      || candidate.title !== expected.title
      || !boundedString(candidate.description, 240)
      || !Array.isArray(candidate.items)
      || candidate.items.length > 100
    ) {
      throw new Error("invalid_roadmap_payload");
    }
    const items = candidate.items.map((roadmapItem) => (
      item(roadmapItem, expected.id, value.current_build as number)
    ));
    for (const roadmapItem of items) {
      if (ids.has(roadmapItem.id)) {
        throw new Error("invalid_roadmap_payload");
      }
      ids.add(roadmapItem.id);
    }
    return {
      id: expected.id,
      title: expected.title,
      description: candidate.description,
      items,
    };
  });
  return {
    currentBuild: value.current_build,
    updatedAt: value.updated_at,
    columns,
  };
}

async function jsonValue(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error("unexpected_response");
  }
  return response.json();
}

export class CloudflareRoadmapRepository implements RoadmapRepository {
  async load(): Promise<RoadmapSnapshot> {
    let response: Response;
    try {
      response = await fetch(apiRoute("/roadmap"), {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
    } catch {
      throw new RoadmapRepositoryError(null);
    }
    if (!response.ok) {
      throw new RoadmapRepositoryError(response.status);
    }
    try {
      return parseRoadmapPayload(await jsonValue(response));
    } catch {
      throw new RoadmapRepositoryError(response.status);
    }
  }
}
